import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  catalogs,
  emptyAddress,
  insureds,
  type Address,
  type InsuredAddress,
  type InsuredAddressInput,
  type InsuredDetail,
  type InsuredInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCpfCnpj,
  formatDate,
  formatPhone,
} from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard, DetailTabs } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { InsuredForm } from './InsuredForm';
import {
  PENDENCIA_PEP,
  PERMISSAO_EDITAR,
  rotuloDoPep,
  rotuloDoRamo,
  rotuloDoTipo,
} from './rotulos';

type Aba = 'dados' | 'complementares' | 'nomes';

const abas: { key: Aba; title: string }[] = [
  { key: 'dados', title: 'Dados Cadastrais' },
  { key: 'complementares', title: 'Dados Complementares' },
  { key: 'nomes', title: 'Nome Fantasia' },
];

const classeDaCaixa =
  'size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25';

function useInvalidar(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['insured', id] });
    void queryClient.invalidateQueries({ queryKey: ['insureds'] });
    void queryClient.invalidateQueries({ queryKey: ['entity-history', 'insured', id] });
  };
}

/**
 * Cadastro Completo do segurado, com as tres abas do OnPoint: Dados Cadastrais,
 * Dados Complementares (enderecos, e tambem telefones e CNAEs, que a API do OnPoint
 * devolve) e Nome Fantasia.
 */
export function CadastroCompleto({ segurado }: { segurado: InsuredDetail }) {
  const [aba, setAba] = useState<Aba>('dados');
  return (
    <div className="flex flex-col gap-4">
      <DetailTabs
        tabs={abas}
        active={aba}
        onChange={setAba}
        label="Cadastro Completo"
      />
      {aba === 'dados' ? <DadosCadastrais segurado={segurado} /> : null}
      {aba === 'complementares' ? (
        <>
          <Enderecos segurado={segurado} />
          <Telefones segurado={segurado} />
          <Cnaes segurado={segurado} />
        </>
      ) : null}
      {aba === 'nomes' ? <NomesFantasia segurado={segurado} /> : null}
    </div>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
        {rotulo}
      </dt>
      <dd className="mt-0.5 text-sm text-ink-900">{children}</dd>
    </div>
  );
}

function DadosCadastrais({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(segurado.id);
  const [editando, setEditando] = useState(false);

  const salvar = useMutation({
    mutationFn: (input: InsuredInput) => insureds.update(segurado.id, input),
    onSuccess: () => {
      toast.success('Cadastro atualizado.');
      setEditando(false);
      invalidar();
    },
  });

  return (
    <DetailCard
      title="Dados Cadastrais"
      action={
        can(PERMISSAO_EDITAR) ? (
          <Button size="sm" variant="secondary" onClick={() => setEditando(true)}>
            Editar
          </Button>
        ) : null
      }
    >
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Dado rotulo="Razao Social">{segurado.legal_name}</Dado>
        <Dado rotulo="Documento">{formatCpfCnpj(segurado.document)}</Dado>
        <Dado rotulo="Ramo">{rotuloDoRamo(segurado.sector)}</Dado>
        <Dado rotulo="Data de registro">{formatDate(segurado.created_at)}</Dado>
        <Dado rotulo="Tipo de pessoa">{rotuloDoTipo(segurado.person_kind)}</Dado>
        <Dado rotulo="E-mail">{segurado.email || '-'}</Dado>
        <Dado rotulo="Matriz">
          {segurado.parent_insured ? (
            <Link
              className="font-medium text-brand-700 underline"
              to={`/segurados/${segurado.parent_insured.id}`}
            >
              {segurado.parent_insured.label}
            </Link>
          ) : (
            '-'
          )}
        </Dado>
        <Dado rotulo="Junta comercial">
          {segurado.commercial_registry ? 'Sim' : 'Nao'}
        </Dado>
        <Dado rotulo="PEP">
          <span title={PENDENCIA_PEP}>{rotuloDoPep(segurado.pep_status)}</span>
          {segurado.pep_checked_at
            ? ` em ${formatCivilDate(segurado.pep_checked_at)}`
            : ''}
        </Dado>
        <Dado rotulo="Documento conferido">
          {segurado.document_unchecked ? 'Aceito sem conferencia' : 'Sim'}
        </Dado>
        <Dado rotulo="Cadastro importado">{segurado.imported ? 'Sim' : 'Nao'}</Dado>
        <Dado rotulo="Cadastrado por">{segurado.created_label || '-'}</Dado>
      </dl>

      <Modal
        open={editando}
        onClose={() => setEditando(false)}
        title="Editar dados cadastrais"
        description={segurado.legal_name}
        size="lg"
      >
        <InsuredForm
          insured={segurado}
          submitting={salvar.isPending}
          error={salvar.error}
          onCancel={() => setEditando(false)}
          onSubmit={(input) => salvar.mutate(input)}
        />
      </Modal>
    </DetailCard>
  );
}

// --- Enderecos -------------------------------------------------------------

interface RascunhoDeEndereco {
  endereco: Address;
  exterior: boolean;
  principal: boolean;
  ativo: boolean;
  ordem: string;
}

function rascunhoDe(atual: InsuredAddress | null): RascunhoDeEndereco {
  if (!atual) {
    return {
      endereco: emptyAddress,
      exterior: false,
      principal: false,
      ativo: true,
      ordem: '0',
    };
  }
  return {
    endereco: {
      zip_code: atual.zip_code,
      street: atual.street,
      number: atual.number,
      complement: atual.complement,
      district: atual.district,
      city: atual.city,
      state: atual.state,
    },
    exterior: atual.international,
    principal: atual.is_main,
    ativo: atual.active,
    ordem: String(atual.position),
  };
}

function pedidoDo(rascunho: RascunhoDeEndereco): InsuredAddressInput {
  const { endereco } = rascunho;
  return {
    zip_code: endereco.zip_code,
    street: endereco.street,
    number: endereco.number,
    complement: endereco.complement ?? '',
    district: endereco.district,
    city: endereco.city,
    // A UF do catalogo nao vale no exterior, e o municipio sai pelo nome.
    state: rascunho.exterior ? '' : endereco.state,
    city_code: '',
    international: rascunho.exterior,
    is_main: rascunho.principal,
    active: rascunho.ativo,
    position: Number.parseInt(rascunho.ordem, 10) || 0,
  };
}

function Enderecos({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(segurado.id);
  const podeEditar = can(PERMISSAO_EDITAR);
  // null fechado; 'novo' inclui; o endereco aberto edita.
  const [aberto, setAberto] = useState<InsuredAddress | 'novo' | null>(null);
  const [rascunho, setRascunho] = useState<RascunhoDeEndereco>(rascunhoDe(null));
  const [excluindo, setExcluindo] = useState<InsuredAddress | null>(null);

  const salvar = useMutation({
    mutationFn: (pedido: InsuredAddressInput) =>
      aberto && aberto !== 'novo'
        ? insureds.updateAddress(segurado.id, aberto.id, pedido)
        : insureds.addAddress(segurado.id, pedido),
    onSuccess: () => {
      toast.success('Endereco salvo.');
      setAberto(null);
      invalidar();
    },
  });

  const excluir = useMutation({
    mutationFn: (endereco: InsuredAddress) =>
      insureds.removeAddress(segurado.id, endereco.id),
    onSuccess: () => {
      toast.success('Endereco excluido.');
      setExcluindo(null);
      invalidar();
    },
    // A recusa do endereco em uso e informacao: o caminho passa a ser desativa-lo.
    onError: (erro: Error) => {
      setExcluindo(null);
      toast.error(erro.message);
    },
  });

  function abrir(alvo: InsuredAddress | 'novo') {
    setRascunho(rascunhoDe(alvo === 'novo' ? null : alvo));
    salvar.reset();
    setAberto(alvo);
  }

  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  return (
    <DetailCard
      title="Enderecos"
      description="O endereco usado em uma operacao nao e excluido: desative-o para tira-lo de negocios novos."
      action={
        podeEditar ? (
          <Button size="sm" onClick={() => abrir('novo')}>
            Adicionar novo
          </Button>
        ) : null
      }
    >
      <DataTable
        columns={[
          {
            key: 'endereco',
            header: 'Endereco',
            render: (endereco: InsuredAddress) => (
              <span>
                {endereco.label}
                {endereco.international ? ' (exterior)' : ''}
              </span>
            ),
          },
          {
            key: 'cep',
            header: 'CEP',
            hideBelow: 'sm',
            render: (endereco: InsuredAddress) => endereco.zip_code || '-',
          },
          {
            key: 'principal',
            header: 'Principal',
            render: (endereco: InsuredAddress) =>
              endereco.is_main ? <Badge tone="brand">Principal</Badge> : '-',
          },
          {
            key: 'ativo',
            header: 'Ativo',
            render: (endereco: InsuredAddress) => (endereco.active ? 'Sim' : 'Nao'),
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (endereco: InsuredAddress) => (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => abrir(endereco)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExcluindo(endereco)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        items={segurado.addresses}
        keyOf={(endereco) => endereco.id}
        empty={<EmptyState title="Nenhum endereco cadastrado" />}
      />

      <Modal
        open={aberto !== null}
        onClose={() => setAberto(null)}
        title={aberto === 'novo' ? 'Adicionar endereco' : 'Editar endereco'}
        description={segurado.legal_name}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAberto(null)}>
              Cancelar
            </Button>
            <Button
              loading={salvar.isPending}
              onClick={() => salvar.mutate(pedidoDo(rascunho))}
            >
              Salvar endereco
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {salvar.error ? (
            <Alert tone="danger" title="Nao foi possivel salvar o endereco">
              {salvar.error.message}
            </Alert>
          ) : null}
          <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-700">
            <input
              type="checkbox"
              className={classeDaCaixa}
              checked={rascunho.exterior}
              onChange={(event) =>
                setRascunho({ ...rascunho, exterior: event.target.checked })
              }
            />
            <span>Endereco no exterior</span>
          </label>
          {rascunho.exterior ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Logradouro no exterior"
                required
                value={rascunho.endereco.street}
                error={fieldErrorOf(apiError, 'street')}
                onChange={(event) =>
                  setRascunho({
                    ...rascunho,
                    endereco: { ...rascunho.endereco, street: event.target.value },
                  })
                }
              />
              <Field
                label="Numero no exterior"
                value={rascunho.endereco.number}
                onChange={(event) =>
                  setRascunho({
                    ...rascunho,
                    endereco: { ...rascunho.endereco, number: event.target.value },
                  })
                }
              />
              <Field
                label="Cidade no exterior"
                required
                value={rascunho.endereco.city}
                error={fieldErrorOf(apiError, 'city')}
                onChange={(event) =>
                  setRascunho({
                    ...rascunho,
                    endereco: { ...rascunho.endereco, city: event.target.value },
                  })
                }
              />
              <Field
                label="Codigo postal"
                value={rascunho.endereco.zip_code}
                onChange={(event) =>
                  setRascunho({
                    ...rascunho,
                    endereco: { ...rascunho.endereco, zip_code: event.target.value },
                  })
                }
              />
            </div>
          ) : (
            <AddressFields
              value={rascunho.endereco}
              onChange={(endereco) => setRascunho({ ...rascunho, endereco })}
            />
          )}
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Ordem do endereco"
              type="number"
              min={0}
              value={rascunho.ordem}
              error={fieldErrorOf(apiError, 'position')}
              onChange={(event) =>
                setRascunho({ ...rascunho, ordem: event.target.value })
              }
            />
            <label className="flex cursor-pointer items-center gap-2 self-end text-xs text-ink-700">
              <input
                type="checkbox"
                className={classeDaCaixa}
                checked={rascunho.principal}
                onChange={(event) =>
                  setRascunho({ ...rascunho, principal: event.target.checked })
                }
              />
              <span>Endereco principal</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 self-end text-xs text-ink-700">
              <input
                type="checkbox"
                className={classeDaCaixa}
                checked={rascunho.ativo}
                onChange={(event) =>
                  setRascunho({ ...rascunho, ativo: event.target.checked })
                }
              />
              <span>Endereco ativo</span>
            </label>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        onConfirm={() => (excluindo ? excluir.mutate(excluindo) : undefined)}
        title="Excluir o endereco?"
        message={`${excluindo?.label ?? ''} sai do cadastro. O endereco usado em operacao nao pode ser excluido.`}
        confirmLabel="Excluir"
        tone="danger"
        loading={excluir.isPending}
      />
    </DetailCard>
  );
}

// --- Telefones -------------------------------------------------------------

function Telefones({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(segurado.id);
  const podeEditar = can(PERMISSAO_EDITAR);
  const [telefone, setTelefone] = useState('');

  const incluir = useMutation({
    mutationFn: () => insureds.addContact(segurado.id, { phone: telefone }),
    onSuccess: () => {
      toast.success('Telefone incluido.');
      setTelefone('');
      invalidar();
    },
  });
  const principal = useMutation({
    mutationFn: (id: string) =>
      insureds.updateContact(segurado.id, id, { is_main: true }),
    onSuccess: invalidar,
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (id: string) => insureds.removeContact(segurado.id, id),
    onSuccess: invalidar,
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <DetailCard title="Telefones">
      <DataTable
        columns={[
          {
            key: 'telefone',
            header: 'Telefone',
            render: (linha: InsuredDetail['contacts'][number]) =>
              formatPhone(linha.phone),
          },
          {
            key: 'principal',
            header: 'Principal',
            render: (linha: InsuredDetail['contacts'][number]) =>
              linha.is_main ? <Badge tone="brand">Principal</Badge> : '-',
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (linha: InsuredDetail['contacts'][number]) => (
                    <div className="flex gap-1">
                      {!linha.is_main ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => principal.mutate(linha.id)}
                        >
                          Tornar principal
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remover.mutate(linha.id)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        items={segurado.contacts}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhum telefone cadastrado" />}
      />
      {podeEditar ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            incluir.mutate();
          }}
        >
          <div className="w-56">
            <Field
              label="Telefone com DDD"
              inputMode="tel"
              value={telefone}
              error={incluir.error instanceof Error ? incluir.error.message : undefined}
              onChange={(event) => setTelefone(event.target.value)}
            />
          </div>
          <Button
            type="submit"
            loading={incluir.isPending}
            disabled={telefone.trim() === ''}
          >
            Incluir telefone
          </Button>
        </form>
      ) : null}
    </DetailCard>
  );
}

// --- CNAEs -----------------------------------------------------------------

function Cnaes({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(segurado.id);
  const podeEditar = can(PERMISSAO_EDITAR);
  const [busca, setBusca] = useState('');

  const opcoes = useQuery({
    queryKey: ['catalogs', 'cnae', busca],
    queryFn: ({ signal }) => catalogs.cnaeCodes(busca, signal),
    enabled: podeEditar && busca.trim().length >= 2,
  });

  const incluir = useMutation({
    mutationFn: (codigo: string) =>
      insureds.addCnae(segurado.id, { cnae_code: codigo }),
    onSuccess: () => {
      toast.success('CNAE incluido.');
      setBusca('');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const principal = useMutation({
    mutationFn: (id: string) =>
      insureds.updateCnae(segurado.id, id, { is_primary: true }),
    onSuccess: invalidar,
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (id: string) => insureds.removeCnae(segurado.id, id),
    onSuccess: invalidar,
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <DetailCard title="CNAEs">
      <DataTable
        columns={[
          {
            key: 'codigo',
            header: 'CNAE',
            render: (linha: InsuredDetail['cnaes'][number]) => (
              <span className="tabular-nums">{linha.cnae_code}</span>
            ),
          },
          {
            key: 'descricao',
            header: 'Descricao',
            render: (linha: InsuredDetail['cnaes'][number]) => linha.description,
          },
          {
            key: 'principal',
            header: 'Principal',
            render: (linha: InsuredDetail['cnaes'][number]) =>
              linha.is_primary ? <Badge tone="brand">Principal</Badge> : '-',
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (linha: InsuredDetail['cnaes'][number]) => (
                    <div className="flex gap-1">
                      {!linha.is_primary ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => principal.mutate(linha.id)}
                        >
                          Tornar principal
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remover.mutate(linha.id)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        items={segurado.cnaes}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhum CNAE cadastrado" />}
      />
      {podeEditar ? (
        <div className="flex flex-col gap-2">
          <div className="w-full sm:w-96">
            <Field
              label="Buscar CNAE"
              placeholder="Codigo ou descricao da atividade"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
            />
          </div>
          {(opcoes.data ?? []).length > 0 ? (
            <ul className="flex flex-col gap-1" aria-label="CNAEs encontrados">
              {(opcoes.data ?? []).map((opcao) => (
                <li
                  key={opcao.code}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="text-xs text-ink-700">
                    {opcao.formatted_code} {opcao.description}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={incluir.isPending}
                    onClick={() => incluir.mutate(opcao.code)}
                  >
                    Incluir
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </DetailCard>
  );
}

// --- Nomes fantasia --------------------------------------------------------

function NomesFantasia({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(segurado.id);
  const podeEditar = can(PERMISSAO_EDITAR);
  const [nome, setNome] = useState('');

  const incluir = useMutation({
    mutationFn: () => insureds.addTradeName(segurado.id, { name: nome }),
    onSuccess: () => {
      toast.success('Nome fantasia incluido.');
      setNome('');
      invalidar();
    },
  });
  const principal = useMutation({
    mutationFn: (id: string) =>
      insureds.updateTradeName(segurado.id, id, { is_main: true }),
    onSuccess: invalidar,
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (id: string) => insureds.removeTradeName(segurado.id, id),
    onSuccess: invalidar,
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <DetailCard
      title="Nome Fantasia"
      description="Os nomes fantasia aparecem para escolha na cotacao."
    >
      <DataTable
        columns={[
          {
            key: 'nome',
            header: 'Nome',
            render: (linha: InsuredDetail['trade_names'][number]) => linha.name,
          },
          {
            key: 'principal',
            header: 'Principal',
            render: (linha: InsuredDetail['trade_names'][number]) =>
              linha.is_main ? <Badge tone="brand">Principal</Badge> : '-',
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (linha: InsuredDetail['trade_names'][number]) => (
                    <div className="flex gap-1">
                      {!linha.is_main ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => principal.mutate(linha.id)}
                        >
                          Tornar principal
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remover.mutate(linha.id)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        items={segurado.trade_names}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhum nome fantasia cadastrado" />}
      />
      {podeEditar ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            incluir.mutate();
          }}
        >
          <div className="w-full sm:w-80">
            <Field
              label="Novo nome fantasia"
              value={nome}
              error={incluir.error instanceof Error ? incluir.error.message : undefined}
              onChange={(event) => setNome(event.target.value)}
            />
          </div>
          <Button
            type="submit"
            loading={incluir.isPending}
            disabled={nome.trim() === ''}
          >
            Incluir nome fantasia
          </Button>
        </form>
      ) : null}
    </DetailCard>
  );
}
